import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast, ToastContainer  } from 'react-toastify';
import { registerUser } from '../../redux/actions/registerAction';
import { resetRegisterState } from '../../redux/slices/registerSlice';
import { useDarkMode } from '../../App';
import { motion } from 'framer-motion';
import { FiUser, FiMail, FiPhone, FiLock, FiArrowRight, FiSmartphone, FiGlobe } from 'react-icons/fi';
import '../AuthShared.css';

const LanguageSwitcher = () => {
    const { i18n } = useTranslation('global');
    const isAR = i18n.language === 'ar';

    const toggleLanguage = () => {
        const newLang = isAR ? 'en' : 'ar';
        i18n.changeLanguage(newLang);
        localStorage.setItem('lang', newLang);
    };

    return (
        <motion.button
            className="auth-lang-switcher"
            onClick={toggleLanguage}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
        >
            <FiGlobe />
            <span>{isAR ? 'English' : 'العربية'}</span>
        </motion.button>
    );
};

const Register = () => {
  const { t, i18n } = useTranslation('global');
  const { isDarkMode } = useDarkMode();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const registerState = useSelector((state) => state.register);
  const { isLoading, success, error, data } = registerState || {};

  const [formValues, setFormValues] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    password_confirmation: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = t('register.title', 'Create Account');
  }, [t]);

  useEffect(() => {
    if (success) {
      toast.success(data?.message || t('register.successMessage', 'Registration successful'));
      const userEmail = formValues.email;
      setTimeout(() => navigate('/verify-otp', { state: { email: userEmail } }), 1000);
    }
  }, [success, data, t, navigate, formValues.email]);

  useEffect(() => {
    if (error) {
      // toast(error);//
      toast( { autoClose: 2000 });
      dispatch(resetRegisterState());
    }
  }, [error, dispatch]);

  useEffect(() => () => {
    dispatch(resetRegisterState());
  }, [dispatch]);

  const handleChange = (e) => {
    setFormValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    const { name, email, phone, password, password_confirmation } = formValues;

    if (!name || !email || !phone || !password || !password_confirmation) {
      toast(t('register.allFieldsRequired', 'Please fill in all fields.'));
      return;
    }

    if (password !== password_confirmation) {
      // toast(t('register.passwordMismatch', 'Passwords do not match.'));/
      toast(t('register.passwordMismatch', 'Passwords do not match.'), { autoClose: 2000 });
      return;
    }

    dispatch(registerUser({ ...formValues, lang: i18n.language }));
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.6, staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5 } }
  };

  return (
    <div className={`auth-page ${isDarkMode ? 'dark' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="auth-bg-overlay"></div>
      <div className="auth-blobs">
        <div className="auth-blob"></div>
        <div className="auth-blob"></div>
        <div className="auth-blob"></div>
      </div>
      <LanguageSwitcher />

      <motion.div 
        className="auth-container" 
        style={{ maxWidth: '420px' }}
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <motion.div className="auth-card" variants={itemVariants}>
          <div className="auth-brand">
            <motion.div className="auth-brand-circle" whileHover={{ scale: 1.05, rotate: 5 }}>
              <FiSmartphone style={{ fontSize: '24px', color: '#fff' }} />
            </motion.div>
            <h1>{t('register.brand', 'ONEPLUS ECOSYSTEM')}</h1>
          </div>

          <div className="auth-header">
            <h2>{t('register.joinUsTitle', 'Join US')}</h2>
            <p>{t('register.subtitle', 'Experience the best of mobile technology')}</p>
          </div>

          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="auth-input-group">
              <label>
                <FiUser /> {t('register.name', 'Full Name')}
              </label>
              <input
                type="text"
                name="name"
                value={formValues.name}
                onChange={handleChange}
                placeholder={t('register.namePlaceholder', 'John Doe')}
                className={submitted && !formValues.name ? 'error' : ''}
              />
            </div>

            <div className="auth-input-group">
              <label>
                <FiMail /> {t('register.email', 'Email Address')}
              </label>
              <input
                type="email"
                name="email"
                value={formValues.email}
                onChange={handleChange}
                placeholder={t('register.emailPlaceholder', 'example@mail.com')}
                className={submitted && !formValues.email ? 'error' : ''}
              />
            </div>

            <div className="auth-input-group">
              <label>
                <FiPhone /> {t('register.phone', 'Phone Number')}
              </label>
              <input
                type="tel"
                name="phone"
                value={formValues.phone}
                onChange={handleChange}
                placeholder={t('register.phonePlaceholder', '+20 123 456 789')}
                className={submitted && !formValues.phone ? 'error' : ''}
              />
            </div>

            <div className="auth-input-group">
              <label>
                <FiLock /> {t('register.password', 'Password')}
              </label>
              <input
                type="password"
                name="password"
                value={formValues.password}
                onChange={handleChange}
                placeholder={t('register.passwordPlaceholder', '••••••••')}
                className={submitted && !formValues.password ? 'error' : ''}
              />
            </div>

            <div className="auth-input-group">
              <label>
                <FiLock /> {t('register.passwordConfirmation', 'Confirm Password')}
              </label>
              <input
                type="password"
                name="password_confirmation"
                value={formValues.password_confirmation}
                onChange={handleChange}
                placeholder={t('register.passwordPlaceholder', '••••••••')}
                className={submitted && !formValues.password_confirmation ? 'error' : ''}
              />
            </div>

            <motion.button 
              type="submit" 
              className="auth-submit-btn" 
              disabled={isLoading}
              whileTap={{ scale: 0.98 }}
            >
              {isLoading ? <span className="auth-spinner"></span> : (
                <>
                  {t('register.createAccount', 'Create Account')} <FiArrowRight />
                </>
              )}
            </motion.button>
          </form>

          <div className="auth-footer">
            <p>
              {t('register.haveAccount', 'Already have an account?')}{' '}
              <span className="auth-link" onClick={() => navigate('/login')}>
                {t('register.loginNow', 'Login Now')}
              </span>
            </p>
            <button className="auth-back-btn" onClick={() => navigate('/')}>
              {t('register.backToHome', 'Back to Home')}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Register;

